import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  TextField,
  Typography,
  Grid,
  CircularProgress,
  Container,
  Paper,
  IconButton,
  InputAdornment,
} from '@mui/material';
import { Helmet } from 'react-helmet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonIcon from '@mui/icons-material/Person';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { styled } from '@mui/material/styles';
import Select from 'react-select';
import validator from 'validator';
import { getDownloadURL, listAll, ref } from 'firebase/storage';
import { storage } from '../../firebase/firebase';

// Components
import Navbar from '../../components/Navbar/Navbar';
import Footer from '../../components/Footer/Footer';
import Sidebar from '../../components/Sidebar/Sidebar';

// Assets
import googleicon from '../../../assets/img/google-icon.svg';
import appleicon from '../../../assets/img/appleLogo.png';
import mailicon from '../../../assets/img/email-icon.svg';

// Constants and utilities
// DOB dropdowns and age utilities removed
import sendCoppaEmail from '../../../../../../shared/Utils/AutenticationApi/sendEmail';
import { environment } from '../../../environments/environment';
import RemoteErrorLog from '../../Utils/api';
import updateUserProfile from 'shared/Utils/UpdateProfileApi/Api';
import { fetchCookieUsers } from '../../Utils/firebase-services';
import { cookiePreferencesWebsite } from '../../../../../../shared/Utils/CookiePreferenceApi/api';
import cookiePreferences from '../../../../../../shared/Utils/CookiePreferenceApi/api';

// Firebase
import {
  EmailAuthProvider,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  linkWithCredential,
  sendEmailVerification,
  signInWithCredential,
  updateProfile,
  signInWithPopup,
  onAuthStateChanged,
  linkWithPopup,
  OAuthProvider,
  getAuth,
  signOut,
} from 'firebase/auth';
import { authen, provider, providerApple } from '../../firebase/firebase';
import { firebaseUserType } from '../../types/types';

// Components
import AlertPopup from '../../components/Alert/AlertPopup';
import CustomModal from '../../components/CustomModal';
import CustomNoResponseModal from '../../components/CustomNoResponseModal';
import DocumentViewerModal from '../../components/DocumentViewerModal';

// Styles
import '../../scss/Form.scss';

// Styled components matching IndividualLogin design
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2, 3),
  borderRadius: theme.spacing(2.5),
  border: '1px solid #e2e8f0',
  background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
  boxShadow: '0 2px 15px rgba(0, 0, 0, 0.06), 0 1px 8px rgba(0, 0, 0, 0.04)',
  marginBottom: theme.spacing(2.5),
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.2s ease',

  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
  },

  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.05)',
    transform: 'translateY(-1px)',
  }
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(1.5),
    backgroundColor: '#fafbfc',
    transition: 'all 0.2s ease',
    fontSize: '0.95rem',

    '&:hover': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
      }
    },

    '&.Mui-focused': {
      backgroundColor: '#ffffff',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#2A9235',
        borderWidth: '2px',
      }
    }
  },

  '& .MuiInputLabel-root': {
    fontSize: '0.95rem',
    fontWeight: 500,
    '&.Mui-focused': {
      color: '#2A9235',
    }
  },

  '& .MuiFormHelperText-root': {
    fontSize: '0.8rem',
    marginTop: theme.spacing(0.5),
  }
}));

const StyledSocialButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: '56px',
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'none',
  fontSize: '1rem',
  fontWeight: 600,
  gap: theme.spacing(2),
  borderRadius: theme.spacing(2),
  border: '2px solid #e2e8f0',
  background: '#ffffff',
  color: '#1a1a1a',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 2px 10px rgba(0, 0, 0, 0.06)',
  
  '&:hover:not(:disabled)': {
    borderColor: '#2A9235',
    background: '#ffffff',
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 20px rgba(42, 146, 53, 0.15)',
    
    '& .social-logo': {
      transform: 'scale(1.05)',
    }
  },

  '&:disabled': {
    opacity: 0.5,
    background: '#f5f5f5',
    cursor: 'not-allowed',
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(2.5),
  padding: theme.spacing(1.75, 4),
  fontSize: '1rem',
  fontWeight: 600,
  textTransform: 'none',
  background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
  color: 'white',
  border: 'none',
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: '0 3px 15px rgba(42, 146, 53, 0.2)',

  '&:hover:not(:disabled)': {
    background: 'linear-gradient(135deg, #1e7a2b 0%, #43a047 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 5px 20px rgba(42, 146, 53, 0.25)',
  },

  '&:disabled': {
    background: '#cccccc',
    color: '#666666',
    cursor: 'not-allowed',
  }
}));

const BackButton = styled(Button)(({ theme }) => ({
  color: '#2A9235',
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.75, 1.5),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.2s ease',
  fontSize: '0.9rem',
  '&:hover': {
    backgroundColor: 'rgba(42, 146, 53, 0.08)',
    transform: 'translateX(-2px)',
  }
}));

const TermsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.2),
  borderRadius: 10,
  background: 'rgba(42, 146, 53, 0.03)',
  border: '1px solid rgba(42, 146, 53, 0.08)',
  marginBottom: theme.spacing(1.5),

  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0.8),
    marginBottom: theme.spacing(1),
  },
}));

const InputIconWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 0.8),
  color: '#2A9235',
  fontSize: '1rem',
}));

const DateSelectContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  alignItems: 'center',

  '& .react-select-container': {
    flex: 1,
    '& .react-select__control': {
      minHeight: 46,
      borderRadius: theme.spacing(1.5),
      border: '1px solid #e2e8f0',
      backgroundColor: '#fafbfc',
      transition: 'all 0.2s ease',

      '&:hover': {
        borderColor: '#2A9235',
        backgroundColor: '#ffffff',
      },

      '&--is-focused': {
        borderColor: '#2A9235',
        borderWidth: '2px',
        boxShadow: '0 3px 15px rgba(42, 146, 53, 0.2)',
      },
    },

    '& .react-select__placeholder': {
      fontSize: '0.95rem',
      color: theme.palette.text.secondary,
    },

    '& .react-select__single-value': {
      fontSize: '0.95rem',
      color: theme.palette.text.primary,
    },

    '& .react-select__menu': {
      zIndex: 9999,
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
    },

    '& .react-select__menu-list': {
      zIndex: 9999,
    },
  },
}));

const PersonalSignUp: React.FC = () => {
  const navigate = useNavigate();

  // Form state
  const [email, setEmail] = useState('');
  const [createPassword, setCreatePassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  /*
  // DOB related state commented out as per request
  const [month, setMonth] = useState<any>(null);
  const [day, setDay] = useState<any>(null);
  const [year, setYear] = useState<any>(null);
  const [userDob, setUserDob] = useState('');
  const [validateDate, setValidateDate] = useState(false);
  const [age, setAge] = useState(0);
  */

  const handleToggleShowPassword = () => setShowPassword((s) => !s);
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  // Validation state
  const [errorStatus, setErrorStatus] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [AgeEnable, setAgeEnable] = useState(true);
  const [emailEnable, setEmailEnable] = useState(false);
  const [firstEnable, setFirstEnable] = useState(false);
  const [lastEnable, setLastEnable] = useState(false);
  const [passEnable, setPassEnable] = useState(false);
  // Terms checkbox state (was missing after DOB removal)
  const [isChecked, setIsChecked] = useState(false);

  // Firebase and loading state
  const [firebaseUser, setFirebaseUser] = useState<firebaseUserType>();
  const [firebaseUserProvider, setFirebaseUserProvider] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [user, setUser] = useState<any>();

  // Modal states
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isQuestionModalVisible, setIsQuestionModalVisible] = useState(false);
  const [apiErrorMessage, setApiErrorMessage] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>(
    'success'
  );
  const [alertMessage, setAlertMessage] = useState('');
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);

  // Select values for dropdowns
  /*
  const [dayValue, setDayValue] = useState<any>(null);
  const [monthValue, setMonthValue] = useState<any>(null);
  const [yearValue, setYearValue] = useState<any>(null);
  */
  const [dayValue, setDayValue] = useState<any>(null);
  const [monthValue, setMonthValue] = useState<any>(null);
  const [yearValue, setYearValue] = useState<any>(null);

  // Document URLs for privacy policy and terms
  const [documentUrls, setDocumentUrls] = useState<{
    privacyPolicy: string | null;
    termsAndConditions: string | null;
  }>({
    privacyPolicy: null,
    termsAndConditions: null,
  });

  // Fetch policy documents from Firebase Storage
  const fetchPages = async () => {
    try {
      const storageRef = await ref(storage, 'pages');
      const result = await listAll(storageRef);
      const urls = await Promise.all(
        result.items.map((pages) => getDownloadURL(pages))
      );

      setDocumentUrls({
        termsAndConditions: urls[2] || null,
        privacyPolicy: urls[1] || null,
      });
    } catch (e) {
      RemoteErrorLog(e, 'PersonalSignUp.tsx');
    }
  };

  useEffect(() => {
    fetchPages();
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(authen, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  /*
  // DOB effect commented out because DOB UI/state removed
  useEffect(() => {
    if (!validateDate && month && day && year) {
      const dob = convertToDateString(month, day, year);
      setUserDob(dob);
    }
  }, [month, day, year, validateDate]);
  */

  useEffect(() => {
    if (firebaseUser) {
      // Only call hideCookieModal for social logins, not for email/password signup
      if (firebaseUserProvider === 'google.com' || firebaseUserProvider === 'apple.com') {
        hideCookieModal();
      }
    }
  }, [firebaseUser]);

  // DOB conversion commented out. Provide a stub to avoid runtime errors.
  /*
  const convertToDateString = (
    month: { label: string; value: number },
    day: { label: string; value: number },
    year: { label: string; value: number }
  ): string => {
    const yearValue = year?.value;
    const monthValue = month?.value?.toString().padStart(2, '0');
    const dayValue = day?.value?.toString().padStart(2, '0');
    return `${yearValue}-${monthValue}-${dayValue}`;
  };
  */
  const convertToDateString = () => {
    // DOB is commented out - returning empty string / null for legacy callers
    return '';
  };

  const validateEmail = (input: string) => {
    setEmail(input);
    if (!input) {
      setErrorStatus('Email is required.');
      setEmailEnable(false);
      return;
    }

    if (validator.isEmail(input)) {
      setEmailEnable(true);
      setErrorStatus('');
    } else {
      setEmailEnable(false);
      setErrorStatus('Please enter a valid email address.');
    }
  };

  const validatePassword = (text: string) => {
    if (text.length < 6) {
      setErrorMessage('The password must be at least 6 characters long.');
      setPassEnable(false);
    } else {
      setPassEnable(true);
      setErrorMessage('');
    }
  };

  /*
  // DOB change handler commented out - DOB removed from UI
  const handleDateChange = (
    selectedMonth: any,
    selectedDay: any,
    selectedYear: any
  ) => {
    // intentionally empty - DOB is not collected
  };
  */

  const handleFirstName = (inputText: string) => {
    setFirstName(inputText);
    if (!inputText.trim()) {
      setFirstEnable(false);
    } else {
      setFirstEnable(true);
    }
  };

  const handleLastName = (inputText1: string) => {
    setLastName(inputText1);
    if (!inputText1.trim()) {
      setLastEnable(false);
    } else {
      setLastEnable(true);
    }
  };

  const handlePolicyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.privacyPolicy) {
      setPrivacyPolicyOpen(true);
    } else {
      console.error('Privacy Policy document not available');
    }
  };

  const handleTermsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (documentUrls.termsAndConditions) {
      setTermsOpen(true);
    } else {
      console.error('Terms & Conditions document not available');
    }
  };

  const cookieApiCall = async (cookieInfo: any) => {
    const token = await (authen?.currentUser as any).getIdToken(true);
    const result = await cookiePreferencesWebsite(
      cookieInfo,
      token,
      environment?.apiUrl
    );
    return result;
  };

  // Cookie API call for Google/Apple sign-in (using createProfile endpoint)
  const cookieApiCallForSignIn = async (cookieInfo: any) => {
    const token = await (authen?.currentUser as any).getIdToken(true);
    const result = await cookiePreferences(
      cookieInfo,
      token,
      environment?.apiUrl
    );
    return result;
  };

  // Hide cookie modal and handle cookie preferences
  const hideCookieModal = async () => {
    // DOB removed - set dob to null
    /*
    const dob = convertToDateString(month, day, year);
    const cookieInfo = {
      uid: firebaseUser?.uid,
      full_name: firebaseUser?.displayName,
      is_anonymous: firebaseUser?.isAnonymous,
      dob:
        firebaseUserProvider == 'google.com' ||
        firebaseUserProvider == 'apple.com'
          ? null
          : dob,
    };
    */
    const cookieInfo = {
      uid: firebaseUser?.uid,
      full_name: firebaseUser?.displayName,
      is_anonymous: firebaseUser?.isAnonymous,
      dob: null,
    };
    let apiResult: any;
    if (cookieInfo.uid) {
      apiResult = await cookieApiCall(cookieInfo);
    }

    if (
      firebaseUserProvider !== 'google.com' &&
      firebaseUserProvider !== 'apple.com'
    ) {
      if (apiResult == 'Error') {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(errorMessage);
        setIsLoading(false);
      } else {
        setIsModalVisible(true);
        setIsLoading(false);
      }
    } else {
      if (apiResult == 'Error') {
        setIsQuestionModalVisible(true);
        setApiErrorMessage(errorMessage);
        setIsLoading(false);
        localStorage.removeItem('userData');
      } else {
        navigate('/home');
      }
    }
  };

  // Handle back navigation
  const handleBack = () => {
    navigate('/signup');
  };

  const upgradeToEmailPassword = async (email: string, password: string) => {
    try {
      const credential = EmailAuthProvider.credential(email, password);
      linkWithCredential(authen.currentUser!, credential)
        .then((usercred) => {
          if (usercred.user) {
            updateProfile(usercred.user, {
              displayName: `${firstName} ${lastName}`,
            })
              .then(async () => {
                // DOB removed - set dob to null in profile update
                /*
                const dob = convertToDateString(month, day, year);
                const updateProfileType = {
                  uid: authen?.currentUser?.uid ? authen?.currentUser?.uid : '',
                  full_name: `${firstName} ${lastName}`,
                  dob: dob,
                };
                */
                const updateProfileType = {
                  uid: authen?.currentUser?.uid ? authen?.currentUser?.uid : '',
                  full_name: `${firstName} ${lastName}`,
                  dob: null,
                };
                 await updateUserProfile(updateProfileType, environment?.apiUrl);
               })
              .then(async () => {
                // Always treat users as non-COPPA (no age checks)
                  await sendEmailVerification(usercred.user);
                  const { uid, isAnonymous, displayName } = usercred.user;
                  const userCookiePrefernce = await fetchCookieUsers(uid);
                  if (userCookiePrefernce === 'Error') {
                    setIsQuestionModalVisible(true);
                    setApiErrorMessage(errorMessage);
                    setIsLoading(false);
                  } else if (userCookiePrefernce === false) {
                    const cookieInfo = {
                      uid: uid,
                      full_name: displayName,
                      is_anonymous: isAnonymous,
                      dob: null,
                    };
                    const apiResult = await cookieApiCallForSignIn(cookieInfo);
                    
                    await signOut(authen);
                    
                    if (apiResult === 'Error') {
                      setIsQuestionModalVisible(true);
                      setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
                      setIsLoading(false);
                    } else {
                      setIsModalVisible(true);
                      setIsLoading(false);
                    }
                  } else {
                    const cookieInfo = {
                      uid: uid,
                      full_name: displayName,
                      is_anonymous: isAnonymous,
                      dob: null,
                    };
                    const apiResult = await cookieApiCallForSignIn(cookieInfo);
                    await signOut(authen);
                    if (apiResult === 'Error') {
                      setIsQuestionModalVisible(true);
                      setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
                      setIsLoading(false);
                    } else {
                      setIsLoading(false);
                      setIsModalVisible(true);
                    }
                   }
              })
              .catch((error) => {
                RemoteErrorLog(error, 'PersonalSignUp.tsx');
              });
          }
        })
        .catch((error) => {
          setIsLoading(false);
          setAlertSeverity('error');
          setAlertMessage(
            "Oops! There's already an account linked to this email address. Please try signing up with a different email ID."
          );
          setAlertOpen(true);
          RemoteErrorLog(error, 'PersonalSignUp.tsx');
        });
    } catch (error) {
      if (error === 'auth/email-already-in-use') {
        setAlertSeverity('error');
        setAlertMessage('Email address is already in use!');
        setAlertOpen(true);
      }
      RemoteErrorLog(error, 'PersonalSignUp.tsx');
    }
  };

  const signUpNewUser = async () => {
    setIsLoading(true);
    if (user?.uid && user?.isAnonymous) {
      upgradeToEmailPassword(email, createPassword);
    } else {
      try {
        createUserWithEmailAndPassword(authen, email, createPassword)
          .then((userCredentials) => {
            if (userCredentials.user) {
              updateProfile(userCredentials.user, {
                displayName: `${firstName} ${lastName}`,
              })
                .then(async () => {
                  // Always treat users as non-COPPA (no age checks)
                    await sendEmailVerification(userCredentials.user);
                    const { uid, isAnonymous, displayName } =
                      userCredentials.user;
                    const cookieInfo = {
                      uid: uid,
                      full_name: displayName,
                      is_anonymous: isAnonymous,
                      dob: null,
                    };
                    const apiResult = await cookieApiCallForSignIn(cookieInfo);
                    await signOut(authen);
                    if (apiResult === 'Error') {
                      setIsQuestionModalVisible(true);
                      setApiErrorMessage("We're currently experiencing some issues. Please try again later.");
                      setIsLoading(false);
                    } else {
                      setIsModalVisible(true);
                      setIsLoading(false);
                    }
                })
                .catch((error) => {
                  RemoteErrorLog(error, 'PersonalSignUp.tsx');
                });
            }
          })
          .catch((error) => {
            setIsLoading(false);
            setAlertSeverity('error');
            setAlertMessage(
              "Oops! There's already an account linked to this email address. Please try signing up with a different email ID."
            );
            setAlertOpen(true);
            RemoteErrorLog(error, 'PersonalSignUp.tsx');
          });
      } catch (error) {
        if (error === 'auth/email-already-in-use') {
          setAlertSeverity('error');
          setAlertMessage('Email address is already in use!');
          setAlertOpen(true);
        }
        RemoteErrorLog(error, 'PersonalSignUp.tsx');
      }
    }
  };

  const handleSignUpClick = async (type = '') => {
    try {
      const isSocialLogin = type === 'google' || type === 'apple';

      if (window.grecaptcha && !isSocialLogin) {
        try {
          await window.grecaptcha.enterprise.ready();
          const token = await window.grecaptcha.enterprise.execute(
            environment?.recaptchaSiteKey,
            { action: 'SIGNUP' }
          );

          const verificationUrl = `https://recaptchaenterprise.googleapis.com/v1/projects/${environment?.firebase?.projectId}/assessments?key=${environment?.firebase?.apiKey}`;

          const response = await fetch(verificationUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: {
                token: token,
                siteKey: environment?.recaptchaSiteKey,
                expectedAction: 'SIGNUP',
              },
            }),
          });

          const result = await response.json();

          if (!result?.tokenProperties?.valid) {
            console.warn(
              'reCAPTCHA verification failed:',
              result?.tokenProperties?.invalidReason
            );
            setAlertSeverity('error');
            setAlertMessage('Security check failed. Please try again.');
            setAlertOpen(true);
            return;
          }
        } catch (recaptchaError) {
          console.error('reCAPTCHA error:', recaptchaError);
          setAlertSeverity('error');
          setAlertMessage('Security check failed. Please try again.');
          setAlertOpen(true);
          return;
        }
      }

      // Proceed with the appropriate sign-in/sign-up flow
      if (type === 'google') {
        await signInWithGoogle();
      } else if (type === 'apple') {
        await signInWithApple();
      } else {
        await signUpNewUser();
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      // Only show generic error if it's not a popup block or auth error
      const errorMessage = error?.message || '';
      const errorCode = error?.code || '';
      if (!errorMessage.includes('popup') && !errorCode.startsWith('auth/')) {
        setAlertSeverity('error');
        setAlertMessage('Failed to sign up. Please try again later.');
        setAlertOpen(true);
      }
      RemoteErrorLog(error, 'PersonalSignUp.tsx');
    }
  };

  const signInWithGoogle = async () => {
    if (user?.uid && user?.isAnonymous) {
      try {
        const result = await linkWithPopup(user, provider);
        setIsLoading(true);
        await updateProfile(result.user, {
          displayName: `${result.user.providerData[0].displayName}`,
        });
        await user.reload();
        const updatedUser = authen.currentUser;
        const updateProfileType = {
          uid: authen?.currentUser?.uid ? authen?.currentUser?.uid : '',
          full_name: `${result.user.providerData[0].displayName}`,
        };
        await updateUserProfile(updateProfileType, environment?.apiUrl);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        const { uid, isAnonymous, displayName } = updatedUser!;
        const userCookiePrefernce = await fetchCookieUsers(uid);
        if (userCookiePrefernce !== true) {
          setFirebaseUser({ uid, isAnonymous, displayName } as any);
        } else {
          setIsLoading(false);
          navigate('/home');
        }
        setFirebaseUserProvider((await result).user.providerData[0].providerId);
      } catch (error: any) {
        if (error?.code === 'auth/credential-already-in-use') {
          setAlertSeverity('error');
          setAlertMessage('Email address is already in use!');
          setAlertOpen(true);
        }
        RemoteErrorLog(error, 'PersonalSignUp.tsx');
      }
    } else {
      try {
        const result = await signInWithPopup(authen, provider);
        const googleCredentials =
          GoogleAuthProvider.credentialFromResult(result);
        const userInfo = signInWithCredential(authen, googleCredentials!);
        setIsLoading(true);
        localStorage.setItem('userData', JSON.stringify((await userInfo).user));
        const { uid, isAnonymous, displayName } = (await userInfo).user;
        const userCookiePrefernce = await fetchCookieUsers(uid);
        if (userCookiePrefernce !== true) {
          // Use createProfile endpoint for Google sign-in
          const cookieInfo = {
            uid: uid,
            full_name: displayName,
            is_anonymous: isAnonymous,
            dob: null,
          };
          const apiResult = await cookieApiCallForSignIn(cookieInfo);
          if (apiResult === 'Error') {
            setIsQuestionModalVisible(true);
            setApiErrorMessage(
              "We're currently experiencing some issues. Please try again later."
            );
            setIsLoading(false);
            localStorage.removeItem('userData');
          } else {
            setIsLoading(false);
            navigate('/home');
          }
        } else {
          setIsLoading(false);
          navigate('/home');
        }
        setFirebaseUserProvider(
          (await userInfo).user.providerData[0].providerId
        );
      } catch (error) {
        RemoteErrorLog(error, 'PersonalSignUp.tsx');
      }
    }
  };

  const signInWithApple = async () => {
    if (user?.uid && user?.isAnonymous) {
      try {
        providerApple.addScope('email');
        providerApple.addScope('name');
        const result = await linkWithPopup(user, providerApple);
        setIsLoading(true);
        await updateProfile(result.user, {
          displayName: `${result.user.providerData[0].displayName}`,
        });
        await user.reload();
        const updatedUser = authen.currentUser;
        const updateProfileType = {
          uid: authen?.currentUser?.uid ? authen?.currentUser?.uid : '',
          full_name: `${result.user.providerData[0].displayName}`,
        };
        await updateUserProfile(updateProfileType, environment?.apiUrl);
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        const { uid, isAnonymous, displayName } = updatedUser!;
        const userCookiePrefernce = await fetchCookieUsers(uid);
        if (userCookiePrefernce !== true) {
          setFirebaseUser({ uid, isAnonymous, displayName } as any);
        } else {
          setIsLoading(false);
          navigate('/home');
        }
        setFirebaseUserProvider((await result).user.providerData[0].providerId);
      } catch (error: any) {
        if (error?.code === 'auth/credential-already-in-use') {
          setAlertSeverity('error');
          setAlertMessage('Email address is already in use!');
          setAlertOpen(true);
        }
        RemoteErrorLog(error, 'PersonalSignUp.tsx');
      }
    } else {
      try {
        providerApple.addScope('email');
        providerApple.addScope('name');
        const result = await signInWithPopup(authen, providerApple);
        const credential = OAuthProvider.credentialFromResult(result);
        const userInfo = signInWithCredential(authen, credential!);
        setIsLoading(true);
        localStorage.setItem('userData', JSON.stringify((await userInfo).user));
        const { uid, isAnonymous, displayName } = (await userInfo).user;
        const userCookiePrefernce = await fetchCookieUsers(uid);
        if (userCookiePrefernce !== true) {
          // Use createProfile endpoint for Apple sign-in
          const cookieInfo = {
            uid: uid,
            full_name: displayName,
            is_anonymous: isAnonymous,
            dob: null,
          };
          const apiResult = await cookieApiCallForSignIn(cookieInfo);
          if (apiResult === 'Error') {
            setIsQuestionModalVisible(true);
            setApiErrorMessage(
              "We're currently experiencing some issues. Please try again later."
            );
            setIsLoading(false);
            localStorage.removeItem('userData');
          } else {
            setIsLoading(false);
            navigate('/home');
          }
        } else {
          setIsLoading(false);
          navigate('/home');
        }
        setFirebaseUserProvider(
          (await userInfo).user.providerData[0].providerId
        );
      } catch (error) {
        RemoteErrorLog(error, 'PersonalSignUp.tsx');
      }
    }
  };

  const handleClose = (event: any, reason: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertOpen(false);
  };

  const hideModal = () => {
    setIsModalVisible(!isModalVisible);
    navigate('/login-individual');
  };

  const hideNoQuestionModal = () => {
    setIsQuestionModalVisible(false);
  };

  return (
    <>
      <Helmet>
        <title>Create Your Personal Account - Spelling Hero</title>
        <meta
          name="description"
          content="Create your personal Spelling Hero account and start your spelling learning journey today."
        />
        <meta
          name="keywords"
          content="spelling games, personal account, individual learning, sign up"
        />
      </Helmet>

      <header>
        <Navbar currentPage="signup" />
      </header>

      <main className="main-container" style={{ paddingTop: 0 }}>
        <section className="quiz-signup">
          <Box sx={{ padding: 0 }}>
            <Grid container spacing={2}>
              <Grid
                item
                lg={5}
                xs={12}
                sm={12}
                sx={{ paddingLeft: 0 }}
                className="sidebar-top-spacing"
              >
                <Sidebar />
              </Grid>

              <Grid item xs={12} sm={12} lg={7} xl={7}>
                <Box sx={{
                  position: 'relative',
                  paddingLeft: { xs: 2, sm: 3, md: 4 },
                  paddingRight: { xs: 2, sm: 3, md: 4 },
                  paddingTop: { xs: 1, md: 0 },
                  paddingBottom: 4,
                  display: 'flex',
                  alignItems: { xs: 'center', md: 'flex-start' },
                  minHeight: { xs: 'auto', md: 'calc(100vh - 180px)' },
                  marginTop: 0
                }}>
                  <Box sx={{
                    position: { xs: 'relative', md: 'absolute' },
                    top: { md: 24 },
                    left: { md: 24 },
                    zIndex: 1,
                    mb: { xs: 2, md: 0 },
                    display: { xs: 'none', md: 'flex' },
                    justifyContent: { md: 'flex-start' }
                  }}>
                    <BackButton startIcon={<ArrowBackIcon />} onClick={handleBack}>
                      Back
                    </BackButton>
                  </Box>

                  <Box sx={{ width: '100%', maxWidth: 920, mx: 'auto', pt: 0 }}>
                    <Container maxWidth="lg" sx={{ py: 1, pt: 2, px: { xs: 2, md: 4 } }}>
                      {/* Loading state */}
                      {isLoading ? (
                        <Box sx={{ 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          py: 8 
                        }}>
                          <CircularProgress size={40} sx={{ color: '#2A9235', mb: 2 }} />
                          <Typography>Creating your account...</Typography>
                        </Box>
                      ) : (
                        <>
                          <Box sx={{ textAlign: 'center', mb: 1 }}>
                            <Typography 
                              variant="h2" 
                              component="h2" 
                              sx={{ 
                                mb: 1, 
                                fontWeight: 700,
                                background: 'linear-gradient(135deg, #2A9235 0%, #4CAF50 100%)',
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                color: 'text.secondary',
                                fontSize: { xs: '1.75rem', md: '2.25rem' }
                              }}
                            >
                              Create Your Account
                            </Typography>
                            
                            <Typography 
                              variant="h5" 
                              sx={{ 
                                color: 'text.secondary',
                                fontWeight: 400,
                                maxWidth: 500,
                                mx: 'auto',
                                lineHeight: 1.4,
                                mb: 2,
                                fontSize: { xs: '0.95rem', md: '1.1rem' }
                              }}
                            >
                              Join thousands of learners improving their spelling skills
                            </Typography>

                            <Box 
                              sx={{ 
                                width: 50, 
                                height: 3, 
                                background: 'linear-gradient(90deg, #2A9235, #4CAF50)',
                                borderRadius: 2,
                                mx: 'auto'
                              }} 
                            />
                          </Box>

                          {/* Social Login Options */}
                            <StyledPaper>
                              <Typography 
                                variant="h6" 
                                sx={{ 
                                  mb: 2, 
                                  fontWeight: 600, 
                                  color: 'text.secondary',
                                  textAlign: 'center',
                                  fontSize: '1.1rem'
                                }}
                              >
                                Quick signup with social accounts
                              </Typography>
                              
                              <Grid container spacing={2} sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                  <StyledSocialButton
                                    onClick={() => handleSignUpClick('google')}
                                    disabled={isLoading}
                                  >
                                    <img 
                                      src={googleicon} 
                                      alt="Google" 
                                      className="social-logo"
                                      style={{ 
                                        height: 24,
                                        width: 24,
                                        transition: 'transform 0.3s ease'
                                      }} 
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      Continue with Google
                                    </Typography>
                                  </StyledSocialButton>
                                </Grid>

                                <Grid item xs={12}>
                                  <StyledSocialButton
                                    onClick={() => handleSignUpClick('apple')}
                                    disabled={isLoading}
                                  >
                                    <img 
                                      src={appleicon} 
                                      alt="Apple" 
                                      className="social-logo"
                                      style={{ 
                                        height: 24,
                                        width: 24,
                                        transition: 'transform 0.3s ease'
                                      }} 
                                    />
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      Continue with Apple
                                    </Typography>
                                  </StyledSocialButton>
                                </Grid>
                              </Grid>
                            </StyledPaper>

                          {/* Email/Password Sign Up */}
                          <StyledPaper>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                mb: 2, 
                                fontWeight: 600, 
                                color: 'text.secondary',
                                textAlign: 'center',
                                fontSize: '1.1rem'
                              }}
                            >
                              or continue with email
                            </Typography>

                            <Grid container spacing={2.5}>
                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label="First Name *"
                                  placeholder="Enter your first name"
                                  value={firstName}
                                  onChange={(e) =>
                                    handleFirstName(e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: <PersonIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label="Last Name *"
                                  placeholder="Enter your last name"
                                  value={lastName}
                                  onChange={(e) =>
                                    handleLastName(e.target.value)
                                  }
                                  InputProps={{
                                    startAdornment: <PersonIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                {/* Date of Birth UI commented out
                                <Typography
                                  variant="body2"
                                  sx={{
                                    mb: 1,
                                    fontWeight: 600,
                                    color: '#1a1a1a',
                                  }}
                                >
                                  Date of Birth *
                                </Typography>
                                <DateSelectContainer>
                                  ...date selects...
                                </DateSelectContainer>
                                {validateDate && (
                                  <Typography
                                    variant="caption"
                                    color="error"
                                    sx={{ mt: 1, display: 'block' }}
                                  >
                                    Please enter a valid date
                                  </Typography>
                                )}
                                */}
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label={
                                    // DOB removed - assume adult label
                                    'Your Email *'
                                  }
                                  type="email"
                                  placeholder="example@email.com"
                                  value={email}
                                  onChange={(e) =>
                                    validateEmail(e.target.value)
                                  }
                                  error={!!errorStatus}
                                  helperText={errorStatus}
                                  InputProps={{
                                    startAdornment: <EmailIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <StyledTextField
                                  fullWidth
                                  label="Create Password *"
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder="Enter at least 6 characters"
                                  value={createPassword}
                                  onChange={(e) => {
                                    setCreatePassword(e.target.value);
                                    validatePassword(e.target.value);
                                  }}
                                  error={!!errorMessage}
                                  helperText={errorMessage}
                                  InputProps={{
                                    startAdornment: (
                                      <InputAdornment position="start">
                                        <LockIcon sx={{ color: '#2A9235', mr: 1.5 }} />
                                      </InputAdornment>
                                    ),
                                    endAdornment: (
                                      <InputAdornment position="end">
                                        <IconButton
                                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                                          onClick={handleToggleShowPassword}
                                          onMouseDown={handleMouseDownPassword}
                                          edge="end"
                                          size="small"
                                        >
                                          {showPassword ? (
                                            <VisibilityOff sx={{ color: '#2A9235' }} />
                                          ) : (
                                            <Visibility sx={{ color: '#2A9235' }} />
                                          )}
                                        </IconButton>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                            </Grid>

                            {/* Terms and Conditions */}
                              <TermsSection sx={{ mb: 2 }}>
                                <FormControlLabel
                                  control={
                                    <Checkbox
                                      checked={isChecked}
                                      onChange={(e) =>
                                        setIsChecked(e.target.checked)
                                      }
                                      sx={{
                                        '&.Mui-checked': {
                                          color: '#2A9235',
                                        },
                                      }}
                                    />
                                  }
                                  label={
                                    <Typography
                                      variant="body2"
                                      sx={{ lineHeight: 1.5 }}
                                    >
                                      By clicking here, I agree to the{' '}
                                      <Button
                                        variant="text"
                                        sx={{
                                          p: 0,
                                          textTransform: 'none',
                                          textDecoration: 'underline',
                                          color: '#2A9235',
                                          fontWeight: 600,
                                          minWidth: 'auto',
                                        }}
                                        onClick={handlePolicyClick}
                                      >
                                        Privacy Policy
                                      </Button>{' '}
                                      and{' '}
                                      <Button
                                        variant="text"
                                        sx={{
                                          p: 0,
                                          textTransform: 'none',
                                          textDecoration: 'underline',
                                          color: '#2A9235',
                                          fontWeight: 600,
                                          minWidth: 'auto',
                                        }}
                                        onClick={handleTermsClick}
                                      >
                                        Terms & Conditions
                                      </Button>
                                      .
                                    </Typography>
                                  }
                                />
                              </TermsSection>

                              {/* Sign Up Button */}
                              <StyledButton
                                fullWidth
                                size="large"
                                onClick={() => handleSignUpClick('')}
                                disabled={
                                  !(
                                    AgeEnable &&
                                     emailEnable &&
                                     passEnable &&
                                     firstEnable &&
                                     lastEnable &&
                                    isChecked
                                   )
                                }
                              >
                                {isLoading ? (
                                  <>
                                    <CircularProgress size={18} sx={{ mr: 2, color: 'white' }} />
                                    Creating Account...
                                  </>
                                ) : (
                                  'Create Account'
                                )}
                              </StyledButton>
                            </StyledPaper>

                          {/* Sign Up Link */}
                          <Box sx={{ 
                            textAlign: 'center',
                            mt: 3,
                            p: 2,
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                            border: '1px solid #e2e8f0'
                          }}>
                            <Typography 
                              variant="body1" 
                              sx={{ 
                                fontSize: '0.95rem',
                                color: 'text.secondary'
                              }}
                            >
                              Already have an account?{' '}
                              <Link
                                to="/login"
                                style={{
                                  color: '#2A9235',
                                  textDecoration: 'none',
                                  fontWeight: 600,
                                }}
                              >
                                Log In
                              </Link>
                            </Typography>
                          </Box>
                        </>
                      )}
                    </Container>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </section>
      </main>

      <Footer />

      {/* Modals */}
      <CustomModal
        isModalVisible={isModalVisible}
        onPress={hideModal}
        buttonText="OK"
        modalTitle="Account Created Successfully"
        text="Please check your email to proceed. We've sent a verification link to your email."
        icon={true}
      />

      <CustomNoResponseModal
        isModalVisible={isQuestionModalVisible}
        onPress={hideNoQuestionModal}
        buttonText="OK"
        modalTitle="Oops!"
        text={apiErrorMessage}
      />

      <AlertPopup
        open={alertOpen}
        handleClose={handleClose}
        severity={alertSeverity}
        message={alertMessage}
      />

      <DocumentViewerModal
        open={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        documentUrl={documentUrls.privacyPolicy}
        title="Privacy Policy"
      />

      <DocumentViewerModal
        open={termsOpen}
        onClose={() => setTermsOpen(false)}
        documentUrl={documentUrls.termsAndConditions}
        title="Terms & Conditions"
      />
    </>
  );
};

export default PersonalSignUp;
